import type { FullAporia, Panel, PanelWithExports, Researcher, WorkflowJob, Event, Aporia } from './types'

const API = '/api'

function authHeaders(token: string, researcherId: string) {
  return {
    'x-session': token,
    'x-researcher-id': researcherId,
  }
}

async function j<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const txt = await r.text().catch(() => '')
    throw new Error(txt || r.statusText)
  }
  return r.json()
}

export async function unlock(passphrase: string, handle: string): Promise<{ researcher: Researcher; token: string }> {
  const res = await fetch(`${API}/auth/unlock`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ passphrase, handle }),
  })
  return j(res)
}

export async function getMe(token: string, rid: string): Promise<Researcher> {
  const res = await fetch(`${API}/me`, { headers: authHeaders(token, rid) })
  return j(res)
}

export async function updatePrefs(token: string, rid: string, prefs: any): Promise<void> {
  await fetch(`${API}/me/prefs`, {
    method: 'POST',
    headers: { ...authHeaders(token, rid), 'content-type': 'application/json' },
    body: JSON.stringify({ prefs }),
  })
}

// Aporias
export async function listAporias(token: string, rid: string): Promise<Aporia[]> {
  const res = await fetch(`${API}/aporias`, { headers: authHeaders(token, rid) })
  const data = await j<{ aporias: Aporia[] }>(res)
  return data.aporias
}

export async function createAporia(token: string, rid: string, data: { title: string; phenomenon: string }): Promise<{ id: string }> {
  const res = await fetch(`${API}/aporias`, {
    method: 'POST',
    headers: { ...authHeaders(token, rid), 'content-type': 'application/json' },
    body: JSON.stringify(data),
  })
  return j(res)
}

export async function getFullAporia(token: string, rid: string, id: string): Promise<FullAporia> {
  const res = await fetch(`${API}/aporias/${id}`, { headers: authHeaders(token, rid) })
  return j(res)
}

export async function updateAporia(token: string, rid: string, id: string, patch: Partial<Aporia>): Promise<void> {
  await fetch(`${API}/aporias/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(token, rid), 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  })
}

export async function addBoundary(token: string, rid: string, aporiaId: string, data: { kind: string; content: string; note?: string }) {
  const res = await fetch(`${API}/aporias/${aporiaId}/boundaries`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(data)
  })
  return j(res)
}

export async function addEmbarrassment(token: string, rid: string, aporiaId: string, level: number, private_note?: string) {
  await fetch(`${API}/aporias/${aporiaId}/embarrassment`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' },
    body: JSON.stringify({ level, private_note }),
  })
}

export async function addCat(token: string, rid: string, aporiaId: string, data: any) {
  await fetch(`${API}/aporias/${aporiaId}/cats`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(data)
  })
}

export async function addValueBehavior(token: string, rid: string, aporiaId: string, data: any) {
  await fetch(`${API}/aporias/${aporiaId}/value-behaviors`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(data)
  })
}

export async function updateValueBehavior(token: string, rid: string, id: string, patch: any) {
  await fetch(`${API}/value-behaviors/${id}`, {
    method: 'PATCH', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(patch)
  })
}

export async function addRegret(token: string, rid: string, aporiaId: string, data: { horizon: string; regret_text?: string; wish_text?: string }) {
  await fetch(`${API}/aporias/${aporiaId}/regrets`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(data)
  })
}

export async function addEdge(token: string, rid: string, aporiaId: string, data: any) {
  await fetch(`${API}/aporias/${aporiaId}/edges`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(data)
  })
}

export async function updateEdge(token: string, rid: string, id: string, patch: any) {
  await fetch(`${API}/edges/${id}`, {
    method: 'PATCH', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(patch)
  })
}

// Panels
export async function listPanels(token: string, rid: string): Promise<PanelWithExports[]> {
  const res = await fetch(`${API}/panels`, { headers: authHeaders(token, rid) })
  const data = await j<{ panels: PanelWithExports[] }>(res)
  return data.panels
}

export async function createPanel(token: string, rid: string, data: any): Promise<{ id: string }> {
  const res = await fetch(`${API}/panels`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' }, body: JSON.stringify(data)
  })
  return j(res)
}

export async function exportPanel(token: string, rid: string, panelId: string, method: string, opts?: any) {
  const res = await fetch(`${API}/panels/${panelId}/export`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' },
    body: JSON.stringify({ method, ...opts }),
  })
  return j(res)
}

export async function revokeExport(token: string, rid: string, exportId: string) {
  await fetch(`${API}/exports/${exportId}/revoke`, {
    method: 'POST', headers: authHeaders(token, rid),
  })
}

// Workflows & privacy
export async function triggerErasure(token: string, rid: string, confirmation: string) {
  const res = await fetch(`${API}/erase`, {
    method: 'POST', headers: { ...authHeaders(token, rid), 'content-type': 'application/json' },
    body: JSON.stringify({ confirmation }),
  })
  return j(res)
}

export async function listJobs(token: string, rid: string): Promise<WorkflowJob[]> {
  const res = await fetch(`${API}/jobs`, { headers: authHeaders(token, rid) })
  const data = await j<{ jobs: WorkflowJob[] }>(res)
  return data.jobs
}

export async function listEvents(token: string, rid: string): Promise<Event[]> {
  const res = await fetch(`${API}/events`, { headers: authHeaders(token, rid) })
  const data = await j<{ events: Event[] }>(res)
  return data.events
}

export async function downloadFullArchive(token: string, rid: string) {
  const res = await fetch(`${API}/archive`, { headers: authHeaders(token, rid) })
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `aporia-archive-${new Date().toISOString().slice(0,10)}.json`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// One-time public link fetch (no auth)
export async function fetchPublicPanel(token: string) {
  const res = await fetch(`/p/${token}`)
  if (!res.ok) throw new Error('expired or revoked')
  return res.text()
}
